<template>
  <div class="relative">
    <div class="mt-1 relative rounded-md shadow-sm">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <CalendarIcon class="h-5 w-5 text-gray-400" aria-hidden="true"/>
      </div>
      <input :id="id"
             class="input-base block w-full sm:leading-5 rounded-md pl-10 pr-12 " :class="$parent.inputClass"
             type="text"
             ref="inputRef"
             :required="required"
             :readonly="!typeable"
             v-model="input"
             v-bind="$attrs"
             :placeholder="placeholder"
             :disabled="disabled"
             :tabindex="disabled ? -1 : 0"
             @keyup="keyUp"
             @blur="renderView()"
             @focus="renderView(initialView)"
             @click="renderView(initialView)"
      />


      <button v-show="modelValue && !disabled" type="button"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              @click="clearModelValue()">
        <XIcon class="h-4 w-4 " aria-hidden="true"/>
      </button>
    </div>
    <transition
        enter-from-class="opacity-0 translate-y-3"
        enter-to-class="opacity-100 translate-y-0"
        enter-active-class="transform transition ease-out duration-200"
        leave-active-class="transform transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-3"
    >
      <div class="z-50" v-show="viewShown === 'day' || viewShown === 'year' || viewShown === 'month'">
        <year-picker
            v-show="viewShown === 'year'"
            v-model:pageDate="pageDate"
            :selected="modelValue"
            :lowerLimit="lowerLimit"
            :upperLimit="upperLimit"
            @select="selectYear"
        />
        <month-picker
            v-show="viewShown === 'month'"
            v-model:pageDate="pageDate"
            :selected="modelValue"
            @select="selectMonth"
            :lowerLimit="lowerLimit"
            :upperLimit="upperLimit"
            :format="monthListFormat"
            :headingFormat="monthHeadingFormat"
            :locale="locale"
            @back="viewShown = 'year'"
        />
        <day-picker
            v-show="viewShown === 'day'"
            v-model:pageDate="pageDate"
            :selected="modelValue"
            :weekStartsOn="weekStartsOn"
            :lowerLimit="lowerLimit"
            :upperLimit="upperLimit"
            :disabledDates="disabledDates"
            :locale="locale"
            :weekdayFormat="weekdayFormat"
            @select="selectDay"
            @back="viewShown = 'month'"
        />
      </div>
    </transition>

  </div>
</template>

<script>

import {defineComponent, ref, computed, watchEffect} from 'vue';
import {parse, isValid, format} from 'date-fns';
import {ru} from 'date-fns/locale'
import YearPicker from './YearPicker.vue';
import MonthPicker from './MonthPicker.vue';
import DayPicker from './DayPicker.vue';
import {mixin} from "@/components/ui/form/mixins/BaseInput";

import {CalendarIcon, XIcon} from '@heroicons/vue/outline'


const TIME_RESOLUTIONS = ['day', 'month', 'year'];
export default defineComponent({
  components: {
    YearPicker,
    MonthPicker,
    DayPicker,
    CalendarIcon,
    XIcon
  },
  mixins: [mixin],
  inheritAttrs: false,
  props: {

    /**
     * `v-model` for selected date
     */
    modelValue: {
      type: Date,
      required: false,
    },
    /**
     * Dates not available for picking
     */
    disabledDates: {
      type: Object,
      required: false,
    },
    /**
     * Upper limit for available dates for picking
     */
    upperLimit: {
      type: Date,
      required: false,
    },
    /**
     * Lower limit for available dates for picking
     */
    lowerLimit: {
      type: Date,
      required: false,
    },
    /**
     * View on which the date picker should open. Can be either `year`, `month`, or `day`
     */
    startingView: {
      type: String,
      required: false,
      default: 'day',
      validate: (v) => typeof v === 'string',
    },
    /**
     * `date-fns`-type formatting for a month view headings
     */
    monthHeadingFormat: {
      type: String,
      required: false,
      default: 'LLLL yyyy',
    },
    /**
     * `date-fns`-type formatting for the month picker view
     */
    monthListFormat: {
      type: String,
      required: false,
      default: 'LLL',
    },
    /**
     * `date-fns`-type formatting for a line of weekdays on day view
     */
    weekdayFormat: {
      type: String,
      required: false,
      default: 'EE',
    },
    /**
     * `date-fns`-type format in which the string in the input should be both
     * parsed and displayed
     */
    inputFormat: {
      type: String,
      required: false,
      default: 'dd.MM.yyyy', //yyyy-MM-dd
    },
    /**
     * [`date-fns` locale object](https://date-fns.org/v2.16.1/docs/I18n#usage).
     * Used in string formatting (see default `monthHeadingFormat`)
     */
    locale: {
      type: Object,
      default: ru
    },
    /**
     * Day on which the week should start.
     *
     * Number from 0 to 6, where 0 is Sunday and 6 is Saturday.
     * Week starts with a Monday (1) by default
     */
    weekStartsOn: {
      type: Number,
      required: false,
      default: 1,
      validator: (value) => [0, 1, 2, 3, 4, 5, 6].includes(value),
    },
    /**
     * Disables datepicker and prevents it's opening
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    /*
     * Allows user to input date manually
     */
    typeable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If set, lower-level views won't show
     */
    minimumView: {
      type: String,
      required: false,
      default: 'day',
      validate: (v) => typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
  },
  emits: {
    'update:modelValue': (value) => value === null || value === undefined || isValid(value),
  },
  setup(props, {emit, attrs}) {
    console.log(attrs)
    const viewShown = ref('none');
    const pageDate = ref(new Date());
    const inputRef = ref(null);
    const input = ref('');
    watchEffect(() => {
      const parsed = parse(input.value, props.inputFormat, new Date());
      if (isValid(parsed)) {
        pageDate.value = parsed;
      }
    });
    watchEffect(() => (input.value =
        props.modelValue && isValid(props.modelValue)
            ? format(props.modelValue, props.inputFormat, {
              locale: props.locale,
            })
            : ''));
    const renderView = (view = 'none') => {
      if (!props.disabled)
        viewShown.value = view;
    };
    watchEffect(() => {
      if (props.disabled)
        viewShown.value = 'none';
    });
    const selectYear = (date) => {
      pageDate.value = date;
      if (props.minimumView === 'year') {
        viewShown.value = 'none';
        emit('update:modelValue', date);
      } else {
        viewShown.value = 'month';
      }
    };
    const selectMonth = (date) => {
      pageDate.value = date;
      if (props.minimumView === 'month') {
        viewShown.value = 'none';
        emit('update:modelValue', date);
      } else {
        viewShown.value = 'day';
      }
    };
    const selectDay = (date) => {
      emit('update:modelValue', date);
      viewShown.value = 'none';
    };
    const clearModelValue = () => {
      emit('update:modelValue', null);

    };
    const keyUp = (event) => {
      const code = event.keyCode ? event.keyCode : event.which;
      // close calendar if escape or enter are pressed
      const closeButton = [
        27,
        13, // enter
      ].includes(code);
      if (closeButton) {
        inputRef.value.blur();
      }
      if (props.typeable) {
        const parsedDate = parse(inputRef.value.value, props.inputFormat, new Date(), {locale: props.locale});
        if (isValid(parsedDate)) {
          input.value = inputRef.value.value;
          emit('update:modelValue', parsedDate);
        }
      }
    };
    const initialView = computed(() => {
      const startingViewOrder = TIME_RESOLUTIONS.indexOf(props.startingView);
      const minimumViewOrder = TIME_RESOLUTIONS.indexOf(props.minimumView);
      return startingViewOrder < minimumViewOrder
          ? props.minimumView
          : props.startingView;
    });
    return {
      input,
      inputRef,
      pageDate,
      renderView,
      selectYear,
      selectMonth,
      selectDay,
      keyUp,
      viewShown,
      clearModelValue,
      initialView,
      log: (e) => console.log(e),
    };
  },
});

</script>
